import { useState } from "react"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { Download } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import type { CampaignRecipient, RecipientOutcome } from "@vienna/contracts"
import { RECIPIENT_OUTCOME_LABELS } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Empty, Header, Hint, Person, SearchBox, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { BackLink } from "@/components/campaigns/primitives"
import { downloadText } from "@/lib/download"
import { formatDateTime } from "@/lib/format"
import { campaignQuery, campaignRecipientsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/campaigns/$campaignId/recipients")({
  validateSearch: z.object({ outcome: z.string().optional() }),
  component: CampaignRecipients,
})

const TABS: Array<["all" | RecipientOutcome, string]> = [
  ["all", "All"],
  ["delivered", "Delivered"],
  ["awaiting_receipt", "Awaiting receipt"],
  ["failed", "Failed"],
  ["unconfirmed", "Unconfirmed"],
  ["skipped", "Skipped"],
  ["excluded", "Excluded"],
  ["queued", "Queued"],
]

/** Screen 29. */
function CampaignRecipients() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const { campaignId } = Route.useParams()
  const { outcome = "all" } = Route.useSearch()
  const navigate = Route.useNavigate()
  const campaign = useSuspenseQuery(campaignQuery(workspace.slug, campaignId)).data
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<CampaignRecipient | null>(null)
  const page = useQuery({ ...campaignRecipientsQuery(workspace.slug, campaignId, { outcome: outcome === "all" ? undefined : outcome, q: search || undefined }), placeholderData: (previous) => previous })
  const rows = page.data?.recipients ?? []
  const counts = page.data?.counts
  const params = { workspace: workspace.slug, campaignId }
  const exportCsv = async () => {
    const res = await fetch(`/api/w/${workspace.slug}/campaigns/${campaignId}/recipients.csv`, { credentials: "include" })
    if (!res.ok) return toast.error("Export needs the customer-data permission")
    downloadText(`${campaign.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-recipients.csv`, await res.text(), "text/csv")
  }
  return (
    <div className="page-wrap au-root">
      <Header title="Campaign recipients" description={`${campaign.name} · ${providerLabel(campaign.provider)}`} back={<BackLink to="/w/$workspace/campaigns/$campaignId/results" params={params}>Results</BackLink>}>
        {can("manage_customer_data") && (
          <Button variant="outline" onClick={() => void exportCsv()}>
            <Download /> Export recipients
          </Button>
        )}
      </Header>
      <Tabs value={outcome} onValueChange={(v) => void navigate({ search: { outcome: v === "all" ? undefined : v } })}>
        <TabsList variant="line" className="au-tab-line">
          {TABS.filter(([key]) => key === "all" || (counts?.[key] ?? 0) > 0 || key === outcome).map(([key, name]) => (
            <TabsTrigger key={key} value={key}>
              {name}
              <span className="ml-1 text-xs text-muted-foreground">{key === "all" ? (page.data?.total ?? 0) : (counts?.[key] ?? 0)}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="au-toolbar">
        <SearchBox value={search} onChange={setSearch} placeholder="Search name or identity…" />
        <span className="ml-auto text-xs text-muted-foreground">
          Showing {rows.length} of {page.data?.total ?? 0} recipients
        </span>
      </div>
      <div className="au-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Person</TableHead>
              <TableHead>{providerLabel(campaign.provider)} identity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Evidence / reason</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelected(row)}>
                <TableCell>
                  <Person name={row.contactName} />
                </TableCell>
                <TableCell className="text-muted-foreground">{row.handle ?? "—"}</TableCell>
                <TableCell>
                  <Status>{RECIPIENT_OUTCOME_LABELS[row.outcome]}</Status>
                </TableCell>
                <TableCell className="text-muted-foreground">{row.evidence}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDateTime(row.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {page.isSuccess && !rows.length && <Empty noun="recipients" />}
      </div>
      <div className="mt-6">
        <Hint>Retries require a fresh eligibility check. An unconfirmed provider outcome must be reconciled before any retry.</Hint>
      </div>
      <Dialog open={!!selected} onOpenChange={(value) => !value && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.contactName}</DialogTitle>
            <DialogDescription>
              {selected?.handle ?? "No channel identity"} · {campaign.name}
            </DialogDescription>
          </DialogHeader>
          <dl className="au-definition">
            <div>
              <dt>Status</dt>
              <dd>{selected ? RECIPIENT_OUTCOME_LABELS[selected.outcome] : ""}</dd>
            </div>
            <div>
              <dt>Evidence</dt>
              <dd>{selected?.evidence}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{selected ? formatDateTime(selected.updatedAt) : ""}</dd>
            </div>
          </dl>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              Close
            </Button>
            {selected?.conversationId && (
              <Button variant="outline" asChild>
                <Link to="/w/$workspace/inbox" params={{ workspace: workspace.slug }} search={{ c: selected.conversationId }}>
                  Open conversation
                </Link>
              </Button>
            )}
            {selected && (
              <Button asChild>
                <Link to="/w/$workspace/contacts/$contactId" params={{ workspace: workspace.slug, contactId: selected.contactId }}>
                  View contact
                </Link>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
